import type { ApiErrorBody } from '@room-mates/shared';
import { ApiError } from './apiError.js';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface HttpClientOptions {
  readonly baseUrl?: string;
  /** Read on every call, so a fresh sign-in takes effect immediately. */
  readonly getToken: () => string | null;
  /** Invoked when the API says the session is gone. */
  readonly onUnauthorized?: () => void;
  readonly fetchImpl?: typeof fetch;
}

export interface HttpClient {
  request<T>(method: HttpMethod, path: string, body?: unknown): Promise<T>;
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  delete(path: string): Promise<void>;
}

/**
 * The single place that talks to the API.
 *
 * Every response is normalised here — success to a typed value, failure to an
 * {@link ApiError} — so no component ever inspects a status code.
 */
export function createHttpClient(options: HttpClientOptions): HttpClient {
  const baseUrl = options.baseUrl ?? '/api';
  const doFetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);

  async function request<T>(method: HttpMethod, path: string, body?: unknown): Promise<T> {
    const token = options.getToken();
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    if (token !== null) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await doFetch(`${baseUrl}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (response.status === 204) {
      return undefined as T;
    }

    const payload: unknown = await response.json().catch(() => null);

    if (!response.ok) {
      const failure = (payload as ApiErrorBody | null)?.error;
      if (failure?.code === 'unauthorized') {
        options.onUnauthorized?.();
      }
      throw new ApiError(
        response.status,
        failure?.code ?? 'internal_error',
        failure?.message ?? `Request failed with status ${response.status}`,
        failure?.details ?? [],
      );
    }

    return payload as T;
  }

  return {
    request,
    get: (path) => request('GET', path),
    post: (path, body) => request('POST', path, body ?? {}),
    put: (path, body) => request('PUT', path, body ?? {}),
    patch: (path, body) => request('PATCH', path, body ?? {}),
    delete: async (path) => {
      await request<void>('DELETE', path);
    },
  };
}
